import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { uploadService } from "src/services/uploadService";
import { usePresignedUpload } from "src/hooks/usePresignedUpload";
import { getCategoryFromFileType } from "src/utils/uploadCategories";

const UploadContext = createContext(null);

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within UploadProvider");
  }
  return context;
};

export const UploadProvider = ({ children }) => {
  const [activeUploads, setActiveUploads] = useState([]);
  const uploadMutation = usePresignedUpload();

  // Update active uploads list (only set state when list or progress actually changed to avoid re-rendering all consumers every 500ms)
  const updateActiveUploads = useCallback(() => {
    const next = uploadService.getActiveUploads();
    setActiveUploads((prev) => {
      if (next.length !== prev.length) return next;
      if (next.length === 0) return prev;
      const unchanged = next.every(
        (n, i) =>
          prev[i] &&
          n.id === prev[i].id &&
          n.overallProgress === prev[i].overallProgress
      );
      return unchanged ? prev : next;
    });
  }, []);

  // Helper to get file type from category
  const getFileTypeFromCategory = (category) => {
    if (category === "chat-message-image") return "image";
    if (category === "chat-message-video") return "video";
    if (category === "chat-message-audio") return "audio";
    return "document";
  };

  // Upload multiple files
  const uploadFiles = useCallback(
    async (files, metadata = {}) => {
      if (!files || files.length === 0) {
        return Promise.resolve([]);
      }

      // Determine categories for each file
      const categories = files.map((file) => getCategoryFromFileType(file));

      // Create upload job
      const uploadId = uploadService.createUpload({
        files,
        categories,
        metadata,
      });

      const upload = uploadService.getUpload(uploadId);
      updateActiveUploads();

      // Upload files SEQUENTIALLY (one by one) instead of in parallel
      const uploadResults = [];
      const uploadErrors = [];

      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        const category = categories[fileIndex];

        try {
          // Update status to uploading
          uploadService.updateStatus(uploadId, "uploading");

          console.log(`Starting upload ${fileIndex + 1}/${files.length}: ${file.name}`);

          // Upload file with progress tracking
          const result = await uploadMutation.mutateAsync({
            file,
            category,
            onProgress: (progress) => {
              uploadService.updateProgress(uploadId, fileIndex, progress);
              updateActiveUploads();
            },
          });

          console.log(`Upload ${fileIndex + 1}/${files.length} completed successfully:`, result);

          // Mark file as completed
          uploadService.markFileCompleted(uploadId, fileIndex, {
            ...result,
            type: getFileTypeFromCategory(category),
          });
          updateActiveUploads();

          uploadResults.push(result);

          // Small delay between uploads to prevent rate limiting
          if (fileIndex < files.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Upload ${fileIndex + 1}/${files.length} failed:`, error);
          
          // Mark file as failed
          uploadService.markFileFailed(uploadId, fileIndex, error);
          updateActiveUploads();
          
          uploadErrors.push({ fileIndex, error });
          
          // Continue with next file instead of stopping completely
          continue;
        }
      }

      // Return results if we have any successful uploads
      if (uploadResults.length > 0) {
        console.log(`Upload complete: ${uploadResults.length}/${files.length} files uploaded successfully`);
        return uploadResults;
      }

      // All uploads failed
      if (uploadErrors.length > 0) {
        throw new Error(`All uploads failed. ${uploadErrors.length} errors occurred.`);
      }

      return [];
    },
    [uploadMutation, updateActiveUploads]
  );

  // Get upload progress
  const getUploadProgress = useCallback((uploadId) => {
    return uploadService.getUpload(uploadId);
  }, []);

  // Cancel upload
  const cancelUpload = useCallback(
    (uploadId) => {
      uploadService.cancelUpload(uploadId);
      updateActiveUploads();
    },
    [updateActiveUploads]
  );

  // Remove upload
  const removeUpload = useCallback(
    (uploadId) => {
      uploadService.removeUpload(uploadId);
      updateActiveUploads();
    },
    [updateActiveUploads]
  );

  // Listen to upload updates
  useEffect(() => {
    const interval = setInterval(() => {
      updateActiveUploads();
    }, 500); // Update every 500ms

    return () => clearInterval(interval);
  }, [updateActiveUploads]);

  const value = useMemo(
    () => ({
      uploadFiles,
      getUploadProgress,
      cancelUpload,
      removeUpload,
      activeUploads,
      isUploading: activeUploads.length > 0,
    }),
    [uploadFiles, getUploadProgress, cancelUpload, removeUpload, activeUploads]
  );

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};
